import { DefaultLabField } from "@/services/backend/samples/components/sample-base-data-default-lab-field";
import { Kind } from "@/services/backend/samples/types/kind";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  PatchRequest,
  updateSamplesWaterBaseDataCache,
  useSamplesWaterBaseDataPatchMutation,
  useSamplesWaterBaseDataShowQuery,
} from "@/services/backend/samples/api/water-base-data-api";
import { WaterBaseData } from "@/services/backend/samples/types/sample-base-data";
import { WaterBaseDataMeasurementsEditor } from "@/services/backend/samples/components/water-base-data-measurements-editor";
import { CardContentSkeleton } from "@/shared/components/card-skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import t from "@/lang/lang";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { TextInputField } from "@/shared/components/field/input-field";
import { Label } from "@/shared/components/ui/label";

/**
 * The patch carrying every editable value. Sending all of them — not just
 * the changed one — keeps a quick edit of a second field from replacing
 * the pending patch of the first one before it is sent.
 */
function patchOf(data: WaterBaseData): PatchRequest {
  return {
    id: data.id,
    label: data.label,
    measurements: data.measurements,
    labId: data.labId,
  };
}

export function WaterBaseDataFields({ id }: { id: UUID }) {
  const {
    data,
    error: dataError,
    isLoading: dataLoading,
  } = useSamplesWaterBaseDataShowQuery({ id });

  const request: PatchRequest = { id };
  const [set, { error }] = useOptimisticDebouncedMutation(
    request,
    updateSamplesWaterBaseDataCache,
    useSamplesWaterBaseDataPatchMutation(),
  );

  if (dataError) {
    return <RTKQueryErrorAlert error={dataError} />;
  }

  if (dataLoading || !data) {
    return <CardContentSkeleton />;
  }

  const edit = (change: Partial<WaterBaseData>) => {
    const next = { ...data, ...change };
    set(patchOf(next), next);
  };

  return (
    <div className="grid gap-4">
      {/* The label is unique per company, so a rename can be rejected
          with a conflict. The measurements are validated as a whole, so
          their validation errors are shown here too. */}
      <RTKQueryErrorAlert error={error} showValidationErrors />
      <TextInputField
        label={t("Bezeichnung")}
        description={t(
          "Unterscheidet die Basisdaten eines Unternehmens voneinander.",
        )}
        text={data.label}
        onTextChange={(label) => edit({ label })}
        errors={inputValidationErrorMessages(error, "label")}
      />
      <div className="grid gap-2">
        <Label>{t("Messungen")}</Label>
        <p className="text-muted-foreground text-sm">
          {t(
            "Eine neue Wasserprobe erhält die Messungen ihrer Wasserart in der angegebenen Reihenfolge.",
          )}
        </p>
        <WaterBaseDataMeasurementsEditor
          measurements={data.measurements}
          onMeasurementsChange={(measurements) => edit({ measurements })}
        />
      </div>
      <DefaultLabField
        kind={Kind.Water}
        labId={data.labId}
        onLabIdChange={(labId) => edit({ labId })}
        errors={inputValidationErrorMessages(error, "labId")}
      />
    </div>
  );
}
